import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Path Finder — FTUE demo (3 essais).
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'path_finder.demo.step1_title',
        hintKey: 'path_finder.demo.step1_hint',
        gridSize: 4,
        start: { row: 0, col: 0 },
        goal: { row: 3, col: 3 },
        checkpoints: [],
    },
    {
        idx: 2,
        titleKey: 'path_finder.demo.step2_title',
        hintKey: 'path_finder.demo.step2_hint',
        gridSize: 4,
        start: { row: 0, col: 0 },
        goal: { row: 3, col: 3 },
        checkpoints: [{ row: 1, col: 2 }],
    },
    {
        idx: 3,
        titleKey: 'path_finder.demo.step3_title',
        hintKey: 'path_finder.demo.step3_hint',
        gridSize: 5,
        start: { row: 0, col: 0 },
        goal: { row: 4, col: 4 },
        checkpoints: [
            { row: 1, col: 2 },
            { row: 3, col: 1 },
        ],
    },
];
function cellEq(a, b) {
    return a.row === b.row && a.col === b.col;
}
function adjacent(a, b) {
    return Math.abs(a.row - b.row) + Math.abs(a.col - b.col) === 1;
}
function cellKey(c) {
    return `${c.row}-${c.col}`;
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const step = DEMO_STEPS[stepIdx];
    const [taps, setTaps] = useState([step.start]);
    const [done, setDone] = useState(false);
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const cells = useMemo(() => {
        const out = [];
        for (let r = 0; r < step.gridSize; r += 1) {
            for (let c = 0; c < step.gridSize; c += 1)
                out.push({ row: r, col: c });
        }
        return out;
    }, [step.gridSize]);
    const checkpointSet = useMemo(() => new Set(step.checkpoints.map(cellKey)), [step.checkpoints]);
    const handleTap = (cell) => {
        if (done)
            return;
        const last = taps[taps.length - 1];
        if (!adjacent(last, cell))
            return;
        if (taps.length >= 2 && cellEq(taps[taps.length - 2], cell)) {
            setTaps(taps.slice(0, -1));
            return;
        }
        const newTaps = [...taps, cell];
        setTaps(newTaps);
        if (cellEq(cell, step.goal)) {
            setDone(true);
        }
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        const nextStep = DEMO_STEPS[stepIdx + 1];
        setTaps([nextStep.start]);
        setDone(false);
    };
    const reset = () => {
        setTaps([step.start]);
        setDone(false);
    };
    const tappedSet = new Set(taps.map(cellKey));
    const last = taps[taps.length - 1];
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('path_finder.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "grid w-full max-w-[300px] gap-s1 border border-cyan-border-faint bg-cyan-surface p-s3", style: {
                    gridTemplateColumns: `repeat(${step.gridSize}, minmax(0, 1fr))`,
                    aspectRatio: '1 / 1',
                }, children: cells.map((cell) => {
                    const k = cellKey(cell);
                    const isStart = cellEq(cell, step.start);
                    const isGoal = cellEq(cell, step.goal);
                    const isCheckpoint = checkpointSet.has(k);
                    const isTapped = tappedSet.has(k);
                    const isLast = last && cellEq(last, cell);
                    let cls = 'bg-cyan-bg text-cyan-text-secondary';
                    if (done && isTapped)
                        cls = 'bg-cyan-core text-cyan-surface';
                    else if (isLast)
                        cls = 'bg-cyan-network text-cyan-surface';
                    else if (isTapped)
                        cls = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                    else if (isStart)
                        cls = 'bg-cyan-core text-cyan-surface';
                    else if (isGoal)
                        cls = 'bg-cyan-network/15 text-cyan-text border border-cyan-network/30';
                    else if (isCheckpoint)
                        cls = 'bg-cyan-bg border border-cyan-network text-cyan-network';
                    return (_jsxs("button", { type: "button", onClick: () => handleTap(cell), disabled: done, className: [
                            'flex aspect-square items-center justify-center text-[10px] transition',
                            cls,
                            done ? 'cursor-not-allowed' : 'hover:scale-105 active:scale-95',
                        ].join(' '), children: [isStart && '◉', isGoal && '★', !isStart && !isGoal && isCheckpoint && '◆'] }, k));
                }) }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [done && (_jsxs(_Fragment, { children: [_jsx("p", { className: "text-[13px] text-cyan-core", children: t('path_finder.demo.feedback_correct') }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })] })), !done && (_jsx("button", { type: "button", onClick: reset, className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: t('path_finder.demo.reset') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map